import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/apiClient";
import { Activity, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("doctor");
  const [form, setForm] = useState({ full_name: "", email: "", password: "", medical_reg_number: "", clinic_name: "", phone: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await register({ ...form, role });
      navigate(user.role === "radiology_centre" ? "/centre" : user.role === "pharmacy" ? "/pharmacy" : "/dashboard");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
            <Activity className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <span className="text-2xl font-bold tracking-tight text-slate-900 font-head">ReferU</span>
        </Link>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          <h1 className="text-2xl font-semibold text-slate-900 font-head">Create your account</h1>
          <p className="text-slate-500 mt-1 text-sm">14-day free trial. No card required.</p>

          <Tabs value={role} onValueChange={setRole} className="mt-5">
            <TabsList className="grid grid-cols-3 w-full rounded-xl">
              <TabsTrigger data-testid="role-doctor" value="doctor" className="rounded-lg text-xs">Doctor</TabsTrigger>
              <TabsTrigger data-testid="role-centre" value="radiology_centre" className="rounded-lg text-xs">Centre</TabsTrigger>
              <TabsTrigger data-testid="role-pharmacy" value="pharmacy" className="rounded-lg text-xs">Pharmacy</TabsTrigger>
            </TabsList>
          </Tabs>

          <form onSubmit={submit} className="mt-5 space-y-4">
            <div>
              <Label className="text-slate-700">{role === "doctor" ? "Full Name" : "Contact Name"}</Label>
              <Input data-testid="reg-name" required value={form.full_name} onChange={set("full_name")} className="mt-1.5 h-12 rounded-xl" />
            </div>            <div>
              <Label className="text-slate-700">Email</Label>
              <Input data-testid="reg-email" type="email" required value={form.email} onChange={set("email")} className="mt-1.5 h-12 rounded-xl" />
            </div>
            <div>
              <Label className="text-slate-700">Password</Label>
              <Input data-testid="reg-password" type="password" required minLength={6} value={form.password} onChange={set("password")} className="mt-1.5 h-12 rounded-xl" />
            </div>
            {role === "doctor" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-700">Reg. Number</Label>
                  <Input data-testid="reg-medreg" value={form.medical_reg_number} onChange={set("medical_reg_number")} className="mt-1.5 h-12 rounded-xl" />
                </div>
                <div>
                  <Label className="text-slate-700">Phone</Label>
                  <Input data-testid="reg-phone" value={form.phone} onChange={set("phone")} className="mt-1.5 h-12 rounded-xl" />
                </div>
              </div>
            )}
            <div>
              <Label className="text-slate-700">{role === "doctor" ? "Clinic Name" : role === "pharmacy" ? "Pharmacy Name" : "Centre Name"}</Label>
              <Input data-testid="reg-clinic" value={form.clinic_name} onChange={set("clinic_name")} className="mt-1.5 h-12 rounded-xl" />
            </div>
            {error && <p data-testid="reg-error" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
            <Button data-testid="reg-submit" type="submit" disabled={loading} className="w-full h-12 rounded-xl bg-blue-600 hover:bg-blue-700">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Create account"}
            </Button>
          </form>
          <p className="text-sm text-slate-500 mt-6 text-center">
            Already have an account? <Link to="/login" className="text-blue-600 font-semibold hover:underline">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
